import { useState, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search, Music, Users, Calendar, Mail as MailIcon, FileText, Briefcase, Gamepad2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { queryClient } from '@/lib/queryClient';
import { supabase } from '@/integrations/supabase/client';
import logoEssencia from '/lovable-uploads/e103bf0a-04c5-4266-926c-c5c3c3d46f35.png';
import SearchModal from '@/components/SearchModal';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;
  const isHome = location.pathname === '/';

  // Sticky inteligente: encolhe ao rolar, oculta ao descer e reaparece ao subir.
  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;

    const update = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      const delta = y - lastY;
      if (Math.abs(delta) > 6) {
        setHidden(delta > 0 && y > 140);
        lastY = y;
      }
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Menu aberto nunca deve esconder o cabeçalho
  useEffect(() => {
    if (isMenuOpen) setHidden(false);
  }, [isMenuOpen]);



  const mainNavItems = [
    { name: 'Início', path: '/', icon: Music },
    { name: 'Bandas', path: '/bandas', icon: Users },
    { name: 'Agenda', path: '/agenda-eventos', icon: Calendar },
    { name: 'Notícias', path: '/noticias', icon: FileText },
    { name: 'Sobre', path: '/sobre', icon: FileText },
  ];

  const moreItems = [
    { name: 'Contrate uma banda', path: '/contrate-banda', icon: Users },
    { name: 'Jogo da Memória', path: '/jogo-memoria', icon: Gamepad2 },
    { name: 'Quiz', path: '/quiz', icon: Briefcase },
    { name: 'Contato', path: '/contato', icon: MailIcon },
  ];

  // Prefetch queries on hover for instant navigation
  const prefetchMap: Record<string, () => void> = {
    '/bandas': () => {
      queryClient.prefetchQuery({
        queryKey: ['bandas-publicas'],
        queryFn: async () => {
          const { data } = await supabase.from('bandas').select('*').eq('visivel_publico', true).order('nome');
          return data || [];
        },
        staleTime: 5 * 60 * 1000,
      });
    },
    '/noticias': () => {
      queryClient.prefetchQuery({
        queryKey: ['noticias-publicas'],
        queryFn: async () => {
          const { data } = await supabase.from('noticias').select('id, titulo, subtitulo, autor, imagem_autor_url, imagem_url, created_at, slug').eq('publicado', true).order('created_at', { ascending: false });
          return data || [];
        },
        staleTime: 5 * 60 * 1000,
      });
    },
  };

  const handleHover = useCallback((path: string) => {
    prefetchMap[path]?.();
  }, []);

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-[background-color,border-color,backdrop-filter,transform] duration-300 ease-out will-change-transform ${
        hidden ? '-translate-y-full' : 'translate-y-0'
      } ${
        isHome && !scrolled
          ? 'bg-transparent border-b border-transparent'
          : 'bg-background/95 backdrop-blur-sm border-b border-border'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className={`flex items-center justify-between transition-[height] duration-300 ease-out ${scrolled ? 'h-14 md:h-16' : 'h-16 md:h-[72px]'}`}>


          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
            <img src={logoEssencia} alt="Essência das Bandas" className="h-10 aspect-[305/100]" width={122} height={40} loading="eager" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6">
            {mainNavItems.map(item => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onMouseEnter={() => handleHover(item.path)}
                  className={`relative flex items-center space-x-1 px-3 py-2 rounded-lg transition-all duration-300 ${isActive(item.path) ? 'text-white font-medium' : 'text-muted-foreground hover:text-white hover:bg-muted/20'}`}
                >
                  <Icon size={16} />
                  <span>{item.name}</span>
                  {isActive(item.path) && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4/5 h-[2px] rounded-full bg-gradient-to-r from-accent to-accent-dark animate-scale-in" />
                  )}
                </Link>
              );
            })}

            {/* More Dropdown */}
            <div className="relative group">
              <button className="flex items-center space-x-1 px-3 py-2 text-muted-foreground hover:text-white transition-colors">
                <span>Mais</span>
              </button>
              <div className="absolute top-full left-0 mt-2 w-52 bg-card border border-border rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 z-50">
                {moreItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.name} to={item.path} className="flex items-center space-x-2 px-4 py-3 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg text-muted-foreground hover:text-white hover:bg-muted/20">
                      <Icon size={14} />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </nav>

          {/* CTA + Search + Connect */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Cadastre seu Evento — CTA primário */}
            <Link to="/cadastre-evento">
              <Button className="bg-gradient-to-r from-accent to-accent-dark text-white hover:opacity-90 transition-all gap-1.5 font-semibold">
                <Plus size={16} />
                Cadastre seu Evento
              </Button>
            </Link>

            {/* Search */}
            <Button onClick={() => setIsSearchModalOpen(true)} variant="outline" size="sm" className="bg-input border-border hover:border-accent">
              <Search size={16} className="mr-1.5" />
              Buscar
            </Button>

            {/* Connect */}
            <Link to="/auth">
              <Button variant="outline" size="sm" className="border-border hover:bg-muted/20">
                <Users size={16} className="mr-1.5" />
                Conecte-se
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 text-muted-foreground hover:text-white transition-colors"
            aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border animate-fade-in max-h-[calc(100vh-80px)] overflow-y-auto">
            <nav className="space-y-1">
              {mainNavItems.map(item => {
                const Icon = item.icon;
                return (
                  <Link key={item.name} to={item.path} onClick={() => setIsMenuOpen(false)} className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all ${isActive(item.path) ? 'text-white bg-primary/20 font-medium' : 'text-muted-foreground hover:text-white hover:bg-muted/20'}`}>
                    <Icon size={16} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}

              <hr className="border-border my-2" />

              {/* CTA Mobile */}
              <Link to="/cadastre-evento" onClick={() => setIsMenuOpen(false)} className="flex items-center space-x-2 px-4 py-3 rounded-lg text-white bg-gradient-to-r from-accent/20 to-accent-dark/20 font-medium">
                <Plus size={16} />
                <span>Cadastre seu Evento</span>
              </Link>

              {moreItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link key={item.name} to={item.path} onClick={() => setIsMenuOpen(false)} className={`flex items-center space-x-2 px-4 py-3 rounded-lg transition-all ${isActive(item.path) ? 'text-white bg-primary/20 font-medium' : 'text-muted-foreground hover:text-white hover:bg-muted/20'}`}>
                    <Icon size={16} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}

              <div className="mt-4 pt-4 border-t border-border">
                <Link to="/auth" onClick={() => setIsMenuOpen(false)}>
                  <Button className="w-full bg-gradient-to-r from-accent to-accent-dark min-h-[48px]">
                    <Users size={16} className="mr-2" />
                    Conecte-se
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>

      <SearchModal isOpen={isSearchModalOpen} onClose={() => setIsSearchModalOpen(false)} />
    </header>
  );
};

export default Header;
